import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { motion } from 'framer-motion';
import { Play, Clock, Award, Users, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';

const benefits = [
    { icon: Play, title: 'Live Instruction', desc: 'Interactive session with real-time Q&A' },
    { icon: Clock, title: '60-90 Minutes', desc: 'Concise, actionable content' },
    { icon: Award, title: 'Certificate', desc: 'Certificate of attendance included' },
    { icon: Users, title: 'Recording Access', desc: "Can't make it live? Get the recording" },
];

const learningPoints = [
    'How AI is reshaping UI/UX design workflows in 2026',
    'The exact Figma setup our agency uses for client projects',
    '3 AI tools that cut design time by 60%',
    'How to build a portfolio that gets you hired',
    'Live Q&A with a working PyrusMedia designer',
];

const faqs = [
    { q: 'Is the masterclass really free?', a: 'Yes, 100% free. No credit card required. We run these sessions to help aspiring designers get started.' },
    { q: 'What if I miss the live session?', a: 'All registrants receive the recording within 24 hours. But we highly recommend attending live for the Q&A.' },
    { q: 'Do I need any prior experience?', a: 'No. The masterclass is designed for beginners and anyone curious about UI/UX design with AI.' },
    { q: 'Will there be a certificate?', a: 'Yes, all attendees receive a digital certificate of completion.' },
];

const testimonials = [
    { name: 'Priya S.', role: 'Graphic Designer', text: 'Learned more in 60 minutes than I did in a 6-month course. The AI tools section was mind-blowing.' },
    { name: 'Rahul M.', role: 'Fresh Graduate', text: 'Finally understood what UI/UX actually is. Signed up for the full course right after.' },
    { name: 'Ananya K.', role: 'Frontend Developer', text: 'The Figma shortcuts alone saved me hours. Highly recommend for anyone in design or dev.' },
];

const FreeMasterclass = () => {
    const [openIndex, setOpenIndex] = useState(null);
    const [form, setForm] = useState({ name: '', email: '', phone: '' });

    const handleSubmit = (e) => {
        e.preventDefault();
        alert('You\'re registered! Check your email for the meeting link.');
        setForm({ name: '', email: '', phone: '' });
    };

    return (
        <>
            <Helmet>
                <title>Free Masterclass — UI/UX Design with AI | PyrusMedia Academy</title>
                <meta name="description" content="Join our free 60-90 minute masterclass on UI/UX Design with AI. Learn the exact framework our agency uses for client projects." />
            </Helmet>

            {/* Hero */}
            <section className="pt-32 pb-20 bg-white text-center relative overflow-hidden">
                <div className="absolute top-20 left-1/4 w-[400px] h-[400px] bg-[#bebd19]/5 rounded-full blur-[120px]" />
                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#bebd19]/30 bg-[#bebd19]/10 mb-6"
                    >
                        <Play size={16} className="text-[#bebd19]" />
                        <span className="text-sm font-semibold text-[#bebd19]">Free Live Session</span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
                    >
                        UI/UX Design with AI<br />
                        <span className="text-[#bebd19]">— Free Masterclass</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-xl text-gray-500 max-w-2xl mx-auto mb-8"
                    >
                        Learn the exact framework our agency uses to design apps for clients. 60-90 minutes, live Q&A included.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="flex flex-wrap justify-center gap-6 text-sm text-gray-500 mb-10"
                    >
                        <span className="flex items-center gap-2"><CheckCircle size={16} className="text-[#bebd19]" /> 200+ attended last session</span>
                        <span className="flex items-center gap-2"><CheckCircle size={16} className="text-[#bebd19]" /> 4.9/5 avg rating</span>
                        <span className="flex items-center gap-2"><CheckCircle size={16} className="text-[#bebd19]" /> No experience needed</span>
                    </motion.div>

                    <Button to="/pricing-batches" className="text-lg px-10 py-4">
                        Reserve Your Free Spot
                    </Button>
                </div>
            </section>

            {/* Benefits */}
            <Section className="bg-gray-50">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    {benefits.map((b, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="bg-white p-6 rounded-2xl border border-gray-200 text-center"
                        >
                            <div className="w-12 h-12 rounded-full bg-[#bebd19]/10 flex items-center justify-center mx-auto mb-4">
                                <b.icon size={20} className="text-[#bebd19]" />
                            </div>
                            <h3 className="font-bold text-gray-900 mb-1">{b.title}</h3>
                            <p className="text-sm text-gray-500">{b.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* What You'll Learn */}
            <Section className="bg-white">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What You'll Learn</h2>
                    <div className="space-y-4">
                        {learningPoints.map((point, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl"
                            >
                                <div className="w-8 h-8 rounded-full bg-[#bebd19] text-black flex items-center justify-center font-bold text-sm shrink-0">
                                    {i + 1}
                                </div>
                                <p className="text-gray-700 pt-1">{point}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* Instructor */}
            <Section className="bg-gray-50">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8">Your Instructor</h2>
                    <div className="bg-white p-8 rounded-2xl border border-gray-200 flex flex-col md:flex-row items-center gap-8">
                        <div className="w-24 h-24 rounded-full bg-[#bebd19]/20 flex items-center justify-center text-3xl font-bold text-[#bebd19] shrink-0">
                            PS
                        </div>
                        <div className="text-left">
                            <h3 className="text-xl font-bold text-gray-900">PyrusMedia Design Team</h3>
                            <p className="text-[#bebd19] font-medium mb-2">Lead UI/UX Designer</p>
                            <p className="text-gray-500">5+ years of experience designing apps and websites for 50+ clients. Trained 1,200+ students in UI/UX design with AI tools.</p>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Testimonials */}
            <Section className="bg-white">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What Attendees Say</h2>
                <div className="grid md:grid-cols-3 gap-6">
                    {testimonials.map((t, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="bg-gray-50 p-6 rounded-2xl border border-gray-200"
                        >
                            <p className="text-gray-600 mb-4 italic">"{t.text}"</p>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#bebd19]/20 flex items-center justify-center font-bold text-[#bebd19] text-sm">
                                    {t.name[0]}
                                </div>
                                <div>
                                    <div className="font-semibold text-gray-900 text-sm">{t.name}</div>
                                    <div className="text-gray-500 text-xs">{t.role}</div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* Registration Form */}
            <Section className="bg-gray-50">
                <div className="max-w-xl mx-auto">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Reserve Your Free Spot</h2>
                    <p className="text-gray-500 text-center mb-8">Next session: <span className="font-semibold text-gray-900">Saturday, 2:00 PM IST</span></p>

                    <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl border border-gray-200 space-y-4">
                        <div>
                            <label className="block text-gray-600 text-sm mb-1">Full Name *</label>
                            <input
                                type="text"
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-600 text-sm mb-1">Email Address *</label>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-600 text-sm mb-1">Phone Number *</label>
                            <input
                                type="tel"
                                required
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                            />
                        </div>
                        <button
                            type="submit"
                            className="w-full bg-[#bebd19] text-black font-bold py-3 rounded-full hover:bg-[#d4d325] transition-colors"
                        >
                            Reserve My Spot — It's Free
                        </button>
                        <p className="text-xs text-gray-400 text-center">No credit card required. We'll send you the meeting link via email.</p>
                    </form>
                </div>
            </Section>

            {/* FAQ */}
            <Section className="bg-white">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Common Questions</h2>
                    <div className="space-y-3">
                        {faqs.map((faq, i) => (
                            <div key={i} className="bg-gray-50 rounded-xl border border-gray-200">
                                <button
                                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                                    className="w-full p-5 flex items-center justify-between text-left"
                                >
                                    <span className="font-semibold text-gray-900">{faq.q}</span>
                                    {openIndex === i ? <ChevronUp className="text-[#bebd19]" /> : <ChevronDown className="text-gray-400" />}
                                </button>
                                {openIndex === i && (
                                    <div className="px-5 pb-5 text-gray-500 border-t border-gray-200 pt-3">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* Final CTA */}
            <Section className="bg-[#bebd19]">
                <div className="text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Don't Miss This Free Session</h2>
                    <p className="text-black/70 text-lg mb-8 max-w-xl mx-auto">Join 200+ students who've already attended. Learn real skills, not theory.</p>
                    <Button to="/pricing-batches" className="bg-black text-white hover:bg-gray-800">
                        Reserve Your Free Spot
                    </Button>
                </div>
            </Section>
        </>
    );
};

export default FreeMasterclass;
